import { GUID } from "@faro-lotv/foundation";
import { IElementGenericImgSheet } from "@faro-lotv/ielement-types";
import { create } from "zustand";

type TagsManagementContext = {
  /** The id of the current active area */
  activeAreaId: GUID | undefined;
  /** A callback to set the active area id in the context */
  setActiveAreaId(id: GUID | undefined): void;
  /** The list of scans/rooms selected by the user */
  selectedIds: GUID[];
  /** A callback to select exactly one scan/room */
  selectId(selectedId: GUID): void;
  /** A callback to select multiple scans/rooms at the same time */
  multiSelectIds(selectedIds: GUID[]): void;
  /** A callback to toggle the selection state of a scans/rooms */
  toggleSelectedId(selectedId: GUID): void;
  /** A callback to clear the list of selected elements */
  clearSelection(): void;
  /** The list of scans/rooms hovered by the user */
  hoveredIds: GUID[];
  /** A callback to hover exactly one scan/room  */
  hoverId(hoveredId: GUID): void;
  /** A callback to hover multiple scans/rooms at the same time */
  multiHoverIds(hoveredIds: GUID[]): void;
  /* A callback to clear the list of hovered elements */
  clearHovering(): void;
  /** The list of visible sheets */
  visibleSheets: IElementGenericImgSheet[];
  /** A callback to update the list of visible sheets */
  setVisibleSheets(visibleSheets: IElementGenericImgSheet[]): void;
  /** A callback to set the visibility of a specific sheet */
  setSheetVisibility(sheet: IElementGenericImgSheet, isVisible: boolean): void;
};

export const useTagsManagementContext = create<TagsManagementContext>()(
  (set, getState) => ({
    activeAreaId: undefined,
    setActiveAreaId: (id: GUID | undefined) => set({ activeAreaId: id }),
    selectedIds: [],
    selectId: (selectedId: GUID) => set({ selectedIds: [selectedId] }),
    multiSelectIds: (selectedIds: GUID[]) => set({ selectedIds }),
    toggleSelectedId: (selectedId: GUID) => {
      const state = getState();
      const selectedIndex = state.selectedIds.findIndex(
        (id) => selectedId === id,
      );
      if (selectedIndex >= 0) {
        const newSelectedIds = [...state.selectedIds];
        newSelectedIds.splice(selectedIndex, 1);
        set({ selectedIds: newSelectedIds });
      } else {
        set({ selectedIds: [...state.selectedIds, selectedId] });
      }
    },
    clearSelection: () => set({ selectedIds: [] }),
    hoveredIds: [],
    hoverId: (hoveredId: GUID) => set({ hoveredIds: [hoveredId] }),
    multiHoverIds: (hoveredIds: GUID[]) => set({ hoveredIds }),
    clearHovering: () => set({ hoveredIds: [] }),
    visibleSheets: [],
    setVisibleSheets: (visibleSheets: IElementGenericImgSheet[]) =>
      set({ visibleSheets }),
    setSheetVisibility: (
      sheet: IElementGenericImgSheet,
      isVisible: boolean,
    ) => {
      const state = getState();
      const selectedIndex = state.visibleSheets.findIndex(
        ({ id }) => sheet.id === id,
      );
      if (selectedIndex >= 0) {
        if (!isVisible) {
          const visibleSheets = [...state.visibleSheets];
          visibleSheets.splice(selectedIndex, 1);
          set({ visibleSheets });
        }
      } else if (isVisible) {
        const visibleSheets = [...state.visibleSheets];
        visibleSheets.push(sheet);
        set({ visibleSheets });
      }
    },
  }),
);
